import AuthService from './AuthService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8082/api';

// Helper function to get headers with auth
const getHeaders = (includeContentType = true) => {
  const headers = {};
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  const authHeader = AuthService.getAuthHeader();
  if (authHeader) {
    headers['Authorization'] = authHeader;
  }
  return headers;
};

/**
 * PromptService - Handles prompt library, versions, test runs, and tool linking
 */
class PromptService {

  // ==================== Prompt CRUD ====================

  /**
   * List prompts with optional filtering
   */
  static async listPrompts({ category, domain, status, search } = {}) {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (domain) params.append('domain', domain);
      if (status) params.append('status', status);
      if (search) params.append('search', search);

      const url = `${API_BASE_URL}/v1/prompts${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error listing prompts:', error);
      throw error;
    }
  }

  /**
   * Get favorite prompts
   */
  static async getFavoritePrompts() {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/favorites`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting favorite prompts:', error);
      throw error;
    }
  }

  /**
   * Get prompt by ID
   */
  static async getPrompt(promptId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/${promptId}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting prompt:', error);
      throw error;
    }
  }

  /**
   * Create a new prompt
   */
  static async createPrompt({ name, description, promptText, category, domain, tags }) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ name, description, promptText, category, domain, tags }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating prompt:', error);
      throw error;
    }
  }

  /**
   * Update a prompt
   */
  static async updatePrompt(promptId, { name, description, promptText, category, domain, tags, status, favorite, changeNotes }) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/${promptId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ name, description, promptText, category, domain, tags, status, favorite, changeNotes }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating prompt:', error);
      throw error;
    }
  }

  /**
   * Delete a prompt
   */
  static async deletePrompt(promptId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/${promptId}`, {
        method: 'DELETE',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error('Error deleting prompt:', error);
      throw error;
    }
  }

  /**
   * Toggle favorite status
   */
  static async togglePromptFavorite(promptId, favorite) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/${promptId}/favorite`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ favorite }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error toggling favorite:', error);
      throw error;
    }
  }

  /**
   * Import prompt from tool
   */
  static async importPromptFromTool(toolId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/import-from-tool/${toolId}`, {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error importing prompt from tool:', error);
      throw error;
    }
  }

  // ==================== Version Management ====================

  /**
   * Get version history for a prompt
   */
  static async getPromptVersions(promptId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/${promptId}/versions`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting prompt versions:', error);
      throw error;
    }
  }

  /**
   * Rollback to a specific version
   */
  static async rollbackPromptVersion(promptId, versionNumber) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/${promptId}/rollback/${versionNumber}`, {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error rolling back version:', error);
      throw error;
    }
  }

  // ==================== Test Runs ====================

  /**
   * Record a test run
   */
  static async recordPromptTestRun(testRunData) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/test-runs`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(testRunData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error recording test run:', error);
      throw error;
    }
  }

  /**
   * Get test runs for a prompt
   */
  static async getPromptTestRuns(promptId, limit = 50) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/${promptId}/test-runs?limit=${limit}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting test runs:', error);
      throw error;
    }
  }

  /**
   * Get test runs for a tool
   */
  static async getToolTestRuns(toolId, limit = 50) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/tools/${toolId}/test-runs?limit=${limit}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting tool test runs:', error);
      throw error;
    }
  }

  /**
   * Rate a test run
   */
  static async ratePromptTestRun(testRunId, rating, notes) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/test-runs/${testRunId}/rate`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ rating, notes }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error rating test run:', error);
      throw error;
    }
  }

  // ==================== Test Cases ====================

  /**
   * Get test cases for a prompt
   */
  static async getPromptTestCases(promptId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/${promptId}/test-cases`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting test cases:', error);
      throw error;
    }
  }

  /**
   * Check/validate variables in a prompt
   * Returns status for each {{varname}} pattern
   */
  static async checkPromptVariables(promptText) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/check-variables`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ promptText }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error checking variables:', error);
      throw error;
    }
  }

  /**
   * Create a test case
   */
  static async createPromptTestCase(promptId, { name, description, userPrompt, expectedBehavior, expectedRouting, sequenceOrder }) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/${promptId}/test-cases`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ name, description, userPrompt, expectedBehavior, expectedRouting, sequenceOrder }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating test case:', error);
      throw error;
    }
  }

  // ==================== Tool-Prompt Linking ====================

  /**
   * Link a tool to a prompt version
   * When linked, the tool will use the managed prompt from prompt_library instead of its inline prompt
   * @param {string} toolId - The tool ID
   * @param {string} promptVersionId - The prompt version ID to link
   * @returns {Promise<Object>} Link result with toolId, toolKey, promptVersionId
   */
  static async linkToolToPromptVersion(toolId, promptVersionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/link-tool/${toolId}/to-version/${promptVersionId}`, {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error linking tool to prompt version:', error);
      throw error;
    }
  }

  /**
   * Unlink a tool from its prompt version
   * The tool will fall back to using its inline prompt
   * @param {string} toolId - The tool ID
   * @returns {Promise<Object>} Unlink result
   */
  static async unlinkToolFromPromptVersion(toolId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/link-tool/${toolId}`, {
        method: 'DELETE',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error unlinking tool from prompt version:', error);
      throw error;
    }
  }

  /**
   * Get the effective prompt for a tool
   * Returns the resolved prompt (from linked version if linked, otherwise inline prompt)
   * @param {string} toolId - The tool ID
   * @returns {Promise<Object>} Object with toolId and effectivePrompt
   */
  static async getToolEffectivePrompt(toolId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/prompts/tools/${toolId}/effective-prompt`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting tool effective prompt:', error);
      throw error;
    }
  }
}

export default PromptService;
